import SortableTableHead, { HeadCell } from "components/Common/Table/SortableTableHead";
import { TableBody, TableRow } from "@mui/material";
import { Fragment, useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import {
	AccountNumberText,
	CubesAvailableText,
	EditText,
	ProductLineText,
	RemoveText,
	RequestDateText,
	TruckNameText
} from "constants/text";
import { LinkButton } from "components/Common/Link";
import { models } from "types/api/viewModels";
import { TruckStatus } from "types/declarations/enums.ts";
import { useHandleTableSort } from "hooks/useHandleTableSort";
import TableSkeleton from "components/Common/Table/TableSkeleton/TableSkeleton.tsx";
import { StyledTableCell, StyledTableWrapper } from "components/Common/Table/StyledTable.styles";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { truckActions } from "features/reducers/truck/truck.ts";
import TruckCubeCountTracker from "../TruckCubeCountTracker/TruckCubeCountTracker.tsx";
import { useDialog } from "hooks/useDialog.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import { useTrucks } from "hooks/useTrucks.ts";

export const openTrucksTableHeadCells = [
	{
		id: "requestedDeliveryDate",
		numeric: false,
		disablePadding: false,
		label: RequestDateText,
		sortable: true
	},
	{
		id: "account",
		numeric: false,
		disablePadding: false,
		label: AccountNumberText,
		sortable: false
	},
	{
		id: "truckName",
		numeric: false,
		disablePadding: false,
		label: TruckNameText,
		sortable: false
	},
	{
		id: "productLine",
		numeric: false,
		disablePadding: false,
		label: ProductLineText,
		sortable: false
	},
	{
		id: "cubesAvailable",
		numeric: false,
		disablePadding: false,
		label: CubesAvailableText,
		sortable: false
	},
	{
		id: "empty",
		numeric: false,
		disablePadding: false,
		label: "",
		sortable: false
	}
] as const satisfies readonly HeadCell[];

const GetOpenTrucksTableRow = (
	truck: models["TruckViewModel"],
	onEdit: (truck: models["TruckViewModel"]) => void,
	openDialog: ReturnType<typeof useDialog>["openDialog"]
) => {
	return (
		<Fragment>
			<StyledTableCell
				data-testid="trucks-table-requestedDeliveryDate"
				fixedWidth="107px"
			>
				{format(truck.requestedDeliveryDate, "MM/dd/yy")}
			</StyledTableCell>
			<StyledTableCell
				data-testid="trucks-table-accountName"
				minWidth="207px"
			>
				{[truck.accountNumber, truck.accountName].filter(Boolean).join(" - ")}
			</StyledTableCell>
			<StyledTableCell
				maxWidth="120px"
				data-testid="trucks-table-truckName"
			>
				{truck.truckName}
			</StyledTableCell>
			<StyledTableCell
				maxWidth="120px"
				data-testid="trucks-table-productLine"
			>
				{truck.productLine?.description}
			</StyledTableCell>
			<StyledTableCell
				data-testid="trucks-table-cubesAvailable"
				fixedWidth="210px"
			>
				<TruckCubeCountTracker truck={truck} />
			</StyledTableCell>
			<StyledTableCell
				fixedWidth="56px"
				isLastIndex
			>
				<LinkButton
					onClick={() => onEdit(truck)}
					data-testid="trucks-table-edit-link"
				>
					{EditText}
				</LinkButton>

				<LinkButton
					variant="remove"
					data-testid="trucks-table-remove-link"
					onClick={() => {
						openDialog({
							dialogStatus: DialogTracking.DELETE_TRUCK,
							dialogId: truck.truckId
						});
					}}
				>
					{RemoveText}
				</LinkButton>
			</StyledTableCell>
		</Fragment>
	);
};

const OpenTrucksTable = () => {
	const { openDialog } = useDialog();
	const { isTrucksLoading, trucks, trucksTrigger } = useTrucks();
	const [openTrucks, setOpenTrucks] = useState<models["TruckViewModel"][] | null>();
	const { getComparator, handleRequestSort, order, orderBy } = useHandleTableSort("requestedDeliveryDate", "asc");
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const sortAndFilterTrucks = useCallback(
		(trucks: models["TruckViewModel"][] | null | undefined) =>
			trucks
				?.filter((truck: models["TruckViewModel"]) => truck.truckStatus === TruckStatus.OPEN)
				.slice()
				.sort(getComparator(order, orderBy)),
		[getComparator, order, orderBy]
	);
	const handleEditTruck = (truck: models["TruckViewModel"]) => {
		dispatch(truckActions.updateTruck(truck));
		navigate("add-order");
	};

	useEffect(() => {
		trucksTrigger();
		setOpenTrucks(trucks);
	}, [trucks, setOpenTrucks, trucksTrigger]);

	if (isTrucksLoading) return <TableSkeleton />;

	return (
		<StyledTableWrapper
			data-testid="open-trucks-table"
			aria-label="Open Trucks Table"
		>
			<SortableTableHead
				orderBy={orderBy}
				order={order}
				onRequestSort={handleRequestSort}
				headCells={openTrucksTableHeadCells}
			/>
			<TableBody>
				{sortAndFilterTrucks(openTrucks)?.map((truck) => (
					<TableRow
						key={truck.truckId}
						data-testid={`trucks-table-row-${truck.truckName}`}
					>
						{GetOpenTrucksTableRow(truck, handleEditTruck, openDialog)}
					</TableRow>
				))}
			</TableBody>
		</StyledTableWrapper>
	);
};

export default OpenTrucksTable;
